import React, { ReactElement } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  IAdditionalItemInformation,
  ISanityBlockContent,
  ISanityLocaleBlockContent,
} from '@rbi-ctg/menu';
import { SanityBlockRenderer } from 'components/sanity-block-renderer';

interface IAdditionalItemInfoProps {
  additionalInfo?: IAdditionalItemInformation | ISanityLocaleBlockContent | null;
  productSize?: string;
  enableStaticMenuAllergens: boolean;
  isBKDEAndGermanLegalRequirement: boolean;
  allergenInformation: ReactElement;
}

const Wrapper = styled.div``;

const StyledText = styled.p`
  font-weight: bold;
  margin: 0;
`;

const StyledSpan = styled.span`
  margin-inline-start: 0.5rem;
  font-weight: normal;
`;

const AdditionalInfoContainer = styled.div`
  p + p {
    margin-block-start: 10px;
  }
`;

export const StyledAdditionalInfo = ({
  additionalInfo,
  enableStaticMenuAllergens,
  isBKDEAndGermanLegalRequirement,
  allergenInformation,
}: IAdditionalItemInfoProps) => {
  const { formatMessage } = useIntl();

  let additionalModifierInformation: ISanityBlockContent[] | [] | undefined;
  let alcoholContentWarningsBlockContent: ISanityBlockContent[] | [] | undefined;
  let storagePreparationInstructionsBlockContent: ISanityBlockContent[] | [] | undefined;

  let additionalItemInfo: IAdditionalItemInformation | undefined;
  if (additionalInfo && 'localeRaw' in additionalInfo) {
    additionalModifierInformation = additionalInfo?.localeRaw;
  } else {
    alcoholContentWarningsBlockContent = additionalInfo
      ? additionalInfo?.additionalInformationBlock1?.localeRaw
      : [];

    storagePreparationInstructionsBlockContent = additionalInfo
      ? additionalInfo?.additionalInformationBlock2?.localeRaw
      : [];
    additionalItemInfo = additionalInfo as IAdditionalItemInformation;
  }

  return (
    <AdditionalInfoContainer>
      <>
        {additionalItemInfo?.additives?.locale ? (
          <StyledText data-testid="additional-info-additives">
            {formatMessage({ id: 'additionalInfoAdditives' })}:{' '}
            <StyledSpan>{additionalItemInfo.additives.locale}</StyledSpan>
          </StyledText>
        ) : null}
        {additionalItemInfo?.ingredients?.locale ? (
          <StyledText data-testid="additional-info-ingredients">
            {formatMessage({ id: 'additionalInfoIngredients' })}:{' '}
            <StyledSpan>{additionalItemInfo.ingredients.locale}</StyledSpan>
          </StyledText>
        ) : null}
        {additionalItemInfo?.producerDetails?.locale ? (
          <StyledText data-testid="additional-info-producer-details">
            {formatMessage({ id: 'additionalInfoProducerDetails' })}:{' '}
            <StyledSpan>{additionalItemInfo.producerDetails.locale}</StyledSpan>
          </StyledText>
        ) : null}
      </>
      <>
        {additionalModifierInformation && (
          <Wrapper data-testid="additional-contents-box">
            <SanityBlockRenderer content={additionalModifierInformation} />
          </Wrapper>
        )}
        {alcoholContentWarningsBlockContent && alcoholContentWarningsBlockContent?.length > 0 ? (
          <Wrapper data-testid="additional-contents-box-1">
            <SanityBlockRenderer content={alcoholContentWarningsBlockContent} />
          </Wrapper>
        ) : null}
        {isBKDEAndGermanLegalRequirement && enableStaticMenuAllergens && allergenInformation}
        {storagePreparationInstructionsBlockContent &&
        storagePreparationInstructionsBlockContent?.length > 0 ? (
          <Wrapper data-testid="additional-contents-box-2">
            <SanityBlockRenderer content={storagePreparationInstructionsBlockContent} />
          </Wrapper>
        ) : null}
      </>
    </AdditionalInfoContainer>
  );
};
